import { useTranslation } from 'react-i18next'
import { Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useFormatters } from '@/lib/hooks/useFormatters'
import { useMoney } from '@/lib/hooks/useMoney'
import type { DownsellImpression } from '@/types/downsell'

interface OfferSummaryProps {
  offer: DownsellImpression
  /** What is charged now; the offer's own quote when omitted (a coupon re-quotes it). */
  amountToCharge?: string
  floorApplied?: boolean
  nextChargeDate?: string | null
  className?: string
}

/** Hours (or minutes, under one) until the offer expires. */
const timeLeft = (expiresAt: string, now = Date.now()) => {
  const ms = new Date(expiresAt).getTime() - now
  if (ms <= 0) return { unit: 'expired' as const, count: 0 }
  const hours = Math.floor(ms / 3_600_000)
  if (hours >= 1) return { unit: 'hours' as const, count: hours }
  return { unit: 'minutes' as const, count: Math.max(1, Math.floor(ms / 60_000)) }
}

/**
 * The "last chance" offer as the buyer reads it (F-39): the add-on, the
 * discount, the struck regular price next to the discounted one, how long the
 * price lasts, when the offer expires and what is charged now.
 */
export function OfferSummary({ offer, amountToCharge, floorApplied, nextChargeDate, className }: OfferSummaryProps) {
  const { t } = useTranslation('billing')
  const { formatDate } = useFormatters()
  const formatMoney = useMoney()
  const money = (value: string | number | undefined) => formatMoney(Number(value) || 0, offer.currency)
  const per = offer.interval === 'YEARLY' ? t('lastChance.perYear') : t('lastChance.perMonth')
  const percent = Number(offer.discountPercent)
  const regular = money(offer.regularAmount)
  const left = timeLeft(offer.expiresAt)
  const floor = floorApplied ?? offer.quote?.floorApplied ?? offer.floorApplied ?? false
  const nowAmount = amountToCharge ?? offer.quote?.amountToCharge ?? offer.amountToCharge
  const nextDate = nextChargeDate ?? offer.quote?.nextChargeDate ?? offer.nextChargeDate ?? null

  const duration =
    offer.priceDuration === 'FIRST_CHARGE'
      ? t('lastChance.duration.firstCharge', { regular: `${regular}${per}` })
      : offer.priceDuration === 'CYCLES'
        ? t('lastChance.duration.cycles', { count: offer.priceCycles || 2, regular: `${regular}${per}` })
        : t('lastChance.duration.forever')

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="font-semibold text-gray-900">{offer.title}</h4>
        <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
          −{Number.isInteger(percent) ? percent : percent.toFixed(2)}%
        </span>
      </div>
      {offer.shortDescription && <p className="text-xs text-gray-500">{offer.shortDescription}</p>}
      <p className="text-sm">
        <span className="mr-1.5 text-gray-400 line-through">
          {regular}
          {per}
        </span>
        <span className="text-lg font-bold text-primary">
          {money(offer.discountedPeriodAmount)}
          <span className="text-sm font-medium">{per}</span>
        </span>
      </p>
      <p className="text-xs text-gray-700">{duration}</p>
      {nowAmount !== undefined && (
        <p className="text-xs text-gray-700">
          {floor
            ? t('lastChance.nowFree', { date: nextDate ? formatDate(nextDate, 'P') : '' })
            : t('lastChance.nowAmount', { amount: money(nowAmount), date: nextDate ? formatDate(nextDate, 'P') : '' })}
        </p>
      )}
      {left.unit !== 'expired' && (
        <p className="flex items-center gap-1 text-xs font-medium text-amber-700">
          <Clock className="h-3.5 w-3.5" />
          {left.unit === 'hours'
            ? t('lastChance.expiresHours', { count: left.count })
            : t('lastChance.expiresMinutes', { count: left.count })}
        </p>
      )}
    </div>
  )
}
